"use strict";

/**
 * Copyright (c) "Neo4j"
 * Neo4j Sweden AB [http://neo4j.com]
 *
 * This file is part of Neo4j.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TransactionExecutor = void 0;
var error_1 = require("../error");
var retry_strategy_1 = require("./retry-strategy");
var DEFAULT_MAX_RETRY_TIME_MS = 30 * 1000; // 30 seconds
var DEFAULT_INITIAL_RETRY_DELAY_MS = 1000; // 1 seconds
var DEFAULT_RETRY_DELAY_MULTIPLIER = 2.0;
var DEFAULT_RETRY_DELAY_JITTER_FACTOR = 0.2;
var TransactionExecutor = /** @class */function () {
  function TransactionExecutor(maxRetryTimeMs, initialRetryDelayMs, multiplier, jitterFactor) {
    this._maxRetryTimeMs = _valueOrDefault(maxRetryTimeMs, DEFAULT_MAX_RETRY_TIME_MS);
    this._initialRetryDelayMs = _valueOrDefault(initialRetryDelayMs, DEFAULT_INITIAL_RETRY_DELAY_MS);
    this._multiplier = _valueOrDefault(multiplier, DEFAULT_RETRY_DELAY_MULTIPLIER);
    this._jitterFactor = _valueOrDefault(jitterFactor, DEFAULT_RETRY_DELAY_JITTER_FACTOR);
    this._inFlightTimeoutIds = [];
    this._verifyAfterConstruction();
  }
  TransactionExecutor.prototype.execute = function (transactionCreator, transactionWork) {
    var _this = this;
    return new Promise(function (resolve, reject) {
      _this._executeTransactionInsidePromise(transactionCreator, transactionWork, resolve, reject);
    }).catch(function (error) {
      var retryStartTimeMs = Date.now();
      var retryDelayMs = _this._initialRetryDelayMs;
      return _this._retryTransactionPromise(transactionCreator, transactionWork, error, retryStartTimeMs, retryDelayMs);
    });
  };
  TransactionExecutor.prototype.close = function () {
    // cancel all existing timeouts to prevent further retries
    this._inFlightTimeoutIds.forEach(function (timeoutId) {
      return clearTimeout(timeoutId);
    });
    this._inFlightTimeoutIds = [];
  };
  TransactionExecutor.prototype._retryTransactionPromise = function (transactionCreator, transactionWork, error, retryStartTime, retryDelayMs) {
    var _this = this;
    var elapsedTimeMs = Date.now() - retryStartTime;
    if (elapsedTimeMs > this._maxRetryTimeMs || !(0, retry_strategy_1.canRetryOn)(error)) {
      return Promise.reject(error);
    }
    return new Promise(function (resolve, reject) {
      var nextRetryTime = _this._computeDelayWithJitter(retryDelayMs);
      var timeoutId = setTimeout(function () {
        // filter out this timeoutId when time has come and function is being executed
        _this._inFlightTimeoutIds = _this._inFlightTimeoutIds.filter(function (id) {
          return id !== timeoutId;
        });
        _this._executeTransactionInsidePromise(transactionCreator, transactionWork, resolve, reject);
      }, nextRetryTime);
      // add newly created timeoutId to the list of all in-flight timeouts
      _this._inFlightTimeoutIds.push(timeoutId);
    }).catch(function (error) {
      var nextRetryDelayMs = retryDelayMs * _this._multiplier;
      return _this._retryTransactionPromise(transactionCreator, transactionWork, error, retryStartTime, nextRetryDelayMs);
    });
  };
  TransactionExecutor.prototype._executeTransactionInsidePromise = function (transactionCreator, transactionWork, resolve, reject) {
    var _this = this;
    var tx;
    try {
      tx = transactionCreator();
    } catch (error) {
      // failed to create a transaction
      reject(error);
      return;
    }
    var resultPromise = this._safeExecuteTransactionWork(tx, transactionWork);
    resultPromise.then(function (result) {
      return _this._handleTransactionWorkSuccess(result, tx, resolve, reject);
    }).catch(function (error) {
      return _this._handleTransactionWorkFailure(error, tx, reject);
    });
  };
  TransactionExecutor.prototype._safeExecuteTransactionWork = function (tx, transactionWork) {
    try {
      var result = transactionWork(tx);
      // user defined callback is supposed to return a promise, but it might not; so to protect against an
      // incorrect API usage we wrap the returned value with a resolved promise; this is effectively a
      // validation step without type checks
      return Promise.resolve(result);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  TransactionExecutor.prototype._handleTransactionWorkSuccess = function (result, tx, resolve, reject) {
    if (tx.isOpen()) {
      // transaction work returned resolved promise and transaction has not been committed/rolled back
      // try to commit the transaction
      tx.commit().then(function () {
        // transaction was committed, return result to the user
        resolve(result);
      }).catch(function (error) {
        // transaction failed to commit, propagate the failure
        reject(error);
      });
    } else {
      // transaction work returned resolved promise and transaction is already committed/rolled back
      // return the result returned by given transaction work
      resolve(result);
    }
  };
  TransactionExecutor.prototype._handleTransactionWorkFailure = function (error, tx, reject) {
    if (tx.isOpen()) {
      // transaction work failed and the transaction is still open, roll it back and propagate the failure
      tx.rollback().catch(function (ignore) {
        // ignore the rollback error
      }).then(function () {
        return reject(error);
      }); // propagate the original error we got from the transaction work
    } else {
      // transaction is already rolled back, propagate the error
      reject(error);
    }
  };
  TransactionExecutor.prototype._computeDelayWithJitter = function (delayMs) {
    var jitter = delayMs * this._jitterFactor;
    var min = delayMs - jitter;
    var max = delayMs + jitter;
    return Math.random() * (max - min) + min;
  };
  TransactionExecutor.prototype._verifyAfterConstruction = function () {
    if (this._maxRetryTimeMs < 0) {
      throw (0, error_1.newError)('Max retry time should be >= 0: ' + this._maxRetryTimeMs);
    }
    if (this._initialRetryDelayMs < 0) {
      throw (0, error_1.newError)('Initial retry delay should >= 0: ' + this._initialRetryDelayMs);
    }
    if (this._multiplier < 1.0) {
      throw (0, error_1.newError)('Multiplier should be >= 1.0: ' + this._multiplier);
    }
    if (this._jitterFactor < 0 || this._jitterFactor > 1) {
      throw (0, error_1.newError)('Jitter factor should be in [0.0, 1.0]: ' + this._jitterFactor);
    }
  };
  return TransactionExecutor;
}();
exports.TransactionExecutor = TransactionExecutor;
function _valueOrDefault(value, defaultValue) {
  if (value || value === 0) {
    return value;
  }
  return defaultValue;
}